import axios from "@util/reconciliationAxios";

class ReconciliationService {
	async getFCPReportProduction({ period_start, period_end } = {}) {
		return await axios({
			url: "/api/reports/fcp/production",
			params: {
				...(period_start ? { period_start } : null),
				...(period_end ? { period_end } : null),
			},
		});
	}

	// Resolves which report period(s) the caller's report should default to when none are
	// explicitly selected - the ONE thing this endpoint still does (see
	// app.reports.rec.router.get_fcp_report_v2_production's own HISTORY OF THIS DECISION, LIQ-2403).
	// It used to also return every variance row for the given/resolved periods (the root cause of a
	// real reported freeze at "select all report periods" scale) and take dca_selections/role/
	// role_value - all removed along with that, since nothing here reads rows anymore. Cheap enough
	// now that most callers don't need cancelToken at all, but it's kept for parity/safety with
	// every other fetch function's own cancel-source pattern.
	//
	// Response also carries is_cold now - GET /cache-status used to be its own separate concurrent
	// call for exactly this one boolean (see RecProductionReport.vue's own fetchCacheStatus, since
	// removed) - folded in here once this endpoint got fast enough to carry it just as early,
	// removing one whole request (plus its own preflight) from the batch a report load fires
	// concurrently, one connection closer to plain HTTP/1.1's 6-per-origin cap.
	//
	// rec's report_periods is a multi-select list of exact "Month Year" values (e.g.
	// "January 2022"), not a date range - see docs/rec-report-frontend-integration.md.
	async getFCPReportV2Production({ report_periods, cancelToken } = {}) {
		return await axios({
			url: "/api/reports/rec/production",
			method: "post",
			data: {
				...(report_periods && report_periods.length ? { report_periods } : null),
			},
			cancelToken,
		});
	}

	// Suggests which role/role_value pair(s) the logged-in caller's own company maps to - a
	// convenience so the frontend knows what to send on every other rec endpoint. NOT an
	// enforcement point - the data endpoints never verify a caller's role/role_value against this,
	// per explicit decision to replicate Lincoln's RLS design (docs/rec-report-rls-replication.md).
	async getFCPReportV2MyRole({ cancelToken } = {}) {
		return await axios({
			url: "/api/reports/rec/my-role",
			cancelToken,
		});
	}

	// Backs the Report Period picker's lower bound - replaces a hardcoded range that used to show
	// every caller the same fixed years/months regardless of their own data.
	// earliest_report_period is scoped server-side by the caller's own JWT-resolved role/role_value
	// (see getFCPReportV2Production's own comment - no role/role_value param here either, same
	// reason). The picker's upper bound is no longer a second field here - it's just today's date,
	// computed client-side (see RecProductionReport.vue's buildReportPeriodYearMonths) rather than
	// a second DB round trip for a value real data can never exceed anyway.
	async getFCPReportV2ReportPeriodRange({ cancelToken } = {}) {
		return await axios({
			url: "/api/reports/rec/report-period-range",
			cancelToken,
		});
	}

	// Backs the "DCA" dropdown - TPA/Broker/Coverholder are every (Party Type, Group Name) pair
	// that co-occurs with the caller's own role/role_value scope (their own exact row excluded).
	// "Underwriter" is handled differently per decision 9 (docs/rec-report-rls-replication.md,
	// from a live demo call): it only ever contains LEAD underwriters, never follow, for every
	// caller - displayed on the frontend as "Lead Underwriter" (see RecProductionReport.vue's
	// dcaSectionLabel), though the key/wire format here is still "Underwriter". For a caller who is
	// themselves an Underwriter, ALL FOUR sections are scoped to their lead-connections only, not
	// their full resolved scope. Response is { party_type: [group names] }, in a fixed display
	// order (Underwriter, Broker, TPA, Coverholder). Scoped server-side from the caller's JWT, no
	// role/role_value param here either - see getFCPReportV2Production's own comment.
	//
	// dca_selections carries the DCA dropdown's OWN currently-checked selections (same
	// "PartyType::GroupName" composite-key shape dcaFilter/getFCPReportV2Production's own
	// dca_selections already use) so each party type's own option list can be cross-filtered by
	// every OTHER currently-selected party type - e.g. selecting a TPA narrows the Underwriter/
	// Broker/Coverholder lists to only what co-occurs with that TPA. POST (not GET) since this now
	// carries a body, same query-string-length reasoning as every other filter-carrying rec
	// endpoint.
	//
	// umr (LIQ-2401): the report's own UMR checkbox-list filter (umrFilter), same shape as every
	// other rec endpoint's umr param - narrows every party type's options to only those that occur
	// on the selected UMR(s). Does NOT also merge in selectedUmrRow/the cross-table transaction-row
	// click the way effectiveUmrAndClaimForVarianceDetails does for the Variance Details table -
	// out of scope for this ticket, which asks specifically about the UMR filter.
	async getFCPReportV2PartyGroups({ dca_selections, umr, report_periods, cancelToken } = {}) {
		return await axios({
			url: "/api/reports/rec/party-groups",
			method: "post",
			data: {
				...(dca_selections && dca_selections.length ? { dca_selections } : null),
				...(report_periods && report_periods.length ? { report_periods } : null),
				...(umr && umr.length ? { umr } : null),
			},
			cancelToken,
		});
	}

	// Shared body-building for both /variance-details endpoints below. No role/role_value here
	// either - verified directly against the live router.py signatures for both endpoints (see
	// getFCPReportV2Production's own comment for why).
	_varianceDetailsBody({
		report_periods,
		umr,
		variance_flag,
		source,
		review_status,
		variance_type,
		account_name,
		claim,
		fcp_status,
		dca_selections,
		group_by_umr,
		total_paid_bdx_min,
		total_paid_bdx_max,
		total_paid_fcp_min,
		total_paid_fcp_max,
		variance_amount_min,
		variance_amount_max,
	} = {}) {
		return {
			...(report_periods && report_periods.length ? { report_periods } : null),
			...(umr && umr.length ? { umr } : null),
			...(variance_flag ? { variance_flag } : null),
			...(source ? { source } : null),
			...(review_status && review_status.length ? { review_status } : null),
			...(variance_type && variance_type.length ? { variance_type } : null),
			...(account_name && account_name.length ? { account_name } : null),
			...(claim && claim.length ? { claim } : null),
			...(fcp_status ? { fcp_status } : null),
			...(dca_selections && dca_selections.length ? { dca_selections } : null),
			// true/false both matter here (unlike the `? {x} : null` truthy checks above) - a plain
			// `group_by_umr ? {...} : null` would silently drop an explicit `false` (the plain-sort
			// case, sent once the caller has clicked a column's own sort control - see
			// RecProductionReport.vue's varianceSortTouched), same reasoning as the
			// `undefined !== total_paid_bdx_min`-style checks below.
			...(undefined !== group_by_umr ? { group_by_umr } : null),
			// Total Paid (BDX)/(REC)/Variance's own real, server-side numeric range filters - see
			// RecProductionReport.vue's varianceDetailsFilterParams.
			...(undefined !== total_paid_bdx_min ? { total_paid_bdx_min } : null),
			...(undefined !== total_paid_bdx_max ? { total_paid_bdx_max } : null),
			...(undefined !== total_paid_fcp_min ? { total_paid_fcp_min } : null),
			...(undefined !== total_paid_fcp_max ? { total_paid_fcp_max } : null),
			...(undefined !== variance_amount_min ? { variance_amount_min } : null),
			...(undefined !== variance_amount_max ? { variance_amount_max } : null),
		};
	}

	// Paginated, sortable, filterable Variance Transaction Details rows - see
	// docs/rec-report-variance-details-endpoint.md. offset (this backend's explicit row-offset
	// override) is what the frontend actually drives pagination with - see
	// RecProductionReport.vue's fetchVarianceDetails - since it accumulates rows in a 200-then-500
	// batch pattern that a plain page number can't express; page_size still controls how many rows
	// come back per call, page is left at its default and effectively unused here.
	//
	// POST with a JSON body, not GET with query params - same reasoning as
	// getFCPReportV2Production (see its own comment): umr/review_status/variance_type/
	// account_name/claim/dca_selections can each be many-valued too, on top of report_periods
	// potentially being every period the caller has, so the same query-string-length risk that hit
	// /production in production applies here just as much.
	async getFCPReportV2VarianceDetails({
		page,
		page_size,
		offset,
		sort_by,
		sort_dir,
		export: exportAll,
		cancelToken,
		...filters
	} = {}) {
		return await axios({
			url: "/api/reports/rec/variance-details",
			method: "post",
			data: {
				...this._varianceDetailsBody(filters),
				...(undefined !== page ? { page } : null),
				...(undefined !== page_size ? { page_size } : null),
				...(undefined !== offset ? { offset } : null),
				sort_by,
				sort_dir,
				...(exportAll ? { export: true } : null),
			},
			cancelToken,
		});
	}

	// Cascading dropdown options for the same seven (now nine, with claim/fcp_status) dimensions -
	// call alongside getFCPReportV2VarianceDetails and re-call whenever any filter changes. POST
	// with a JSON body - same reasoning as getFCPReportV2VarianceDetails above. cancelToken - same
	// reasoning/shape as getFCPReportV2Production's own - lets RecProductionReport.vue's own
	// fetchVarianceFilterOptions abort a still-in-flight call before starting a newer one.
	async getFCPReportV2VarianceFilterOptions({ cancelToken, ...filters } = {}) {
		return await axios({
			url: "/api/reports/rec/variance-details/filter-options",
			method: "post",
			data: this._varianceDetailsBody(filters),
			cancelToken,
		});
	}

	// LIQ-2403: backs the REC UMRs panel's per-UMR/period status rollup and the "FCP Accounts" KPI
	// tile - see router.get_rec_umr_rollup's own docstring. Same seven cascading filter dimensions
	// as _varianceDetailsBody (group_by_umr/the numeric range fields simply stay undefined and get
	// omitted, same convention as every other optional field there - this endpoint has no sort/
	// pagination/range-slider concept of its own).
	async getFCPReportV2RecUmrRollup({ cancelToken, ...filters } = {}) {
		return await axios({
			url: "/api/reports/rec/rec-umrs",
			method: "post",
			data: this._varianceDetailsBody(filters),
			cancelToken,
		});
	}

	// Shared body-building for /bdx-details and /cashbook-details - narrower than
	// _varianceDetailsBody on the exact-match dimensions (no variance_flag/source/review_status/
	// variance_type/fcp_status, which don't exist at these two grids' own grain - see router.py's
	// BdxCashbookDetailsRequestBase), but carries every possible sort/search field from BOTH grids -
	// a field one of the two grids doesn't have (e.g. narrative on a /bdx-details call) simply
	// stays undefined on that call and is omitted below, same convention as every other optional
	// field here. Real server-side search (section_number/limeiq_key/total_paid_bdx/paid_tpa_fees/
	// fcp_start_date/narrative/debit/credit), NOT client-side like Variance's own column search -
	// see RecProductionReport.vue's BDX_HEADER_SEARCH_FIELD_KEYS/CASHBOOK_HEADER_SEARCH_FIELD_KEYS.
	_bdxCashbookDetailsBody({
		report_periods,
		umr,
		account_name,
		claim,
		dca_selections,
		sort_by,
		sort_dir,
		tpa,
		coverholder,
		section_number,
		limeiq_key,
		total_paid_bdx_min,
		total_paid_bdx_max,
		paid_tpa_fees_min,
		paid_tpa_fees_max,
		fcp_start_date_from,
		fcp_start_date_to,
		narrative,
		debit_min,
		debit_max,
		credit_min,
		credit_max,
		export: exportAll,
	} = {}) {
		return {
			...(report_periods && report_periods.length ? { report_periods } : null),
			...(umr && umr.length ? { umr } : null),
			...(account_name && account_name.length ? { account_name } : null),
			...(claim && claim.length ? { claim } : null),
			...(dca_selections && dca_selections.length ? { dca_selections } : null),
			...(undefined !== sort_by ? { sort_by } : null),
			...(undefined !== sort_dir ? { sort_dir } : null),
			// BDX-only TPA/Coverholder column checklists (LIQ-2402).
			...(tpa && tpa.length ? { tpa } : null),
			...(coverholder && coverholder.length ? { coverholder } : null),
			...(section_number ? { section_number } : null),
			...(limeiq_key ? { limeiq_key } : null),
			...(undefined !== total_paid_bdx_min ? { total_paid_bdx_min } : null),
			...(undefined !== total_paid_bdx_max ? { total_paid_bdx_max } : null),
			...(undefined !== paid_tpa_fees_min ? { paid_tpa_fees_min } : null),
			...(undefined !== paid_tpa_fees_max ? { paid_tpa_fees_max } : null),
			...(fcp_start_date_from ? { fcp_start_date_from } : null),
			...(fcp_start_date_to ? { fcp_start_date_to } : null),
			...(narrative ? { narrative } : null),
			...(undefined !== debit_min ? { debit_min } : null),
			...(undefined !== debit_max ? { debit_max } : null),
			...(undefined !== credit_min ? { credit_min } : null),
			...(undefined !== credit_max ? { credit_max } : null),
			...(exportAll ? { export: true } : null),
		};
	}

	// Paginated "BDX Detail by Transaction" grid - see getFCPReportV2VarianceDetails's own comment
	// for why POST/JSON body (same query-string-length risk applies here too).
	async getFCPReportV2BdxDetails({ page, page_size, offset, cancelToken, ...filters } = {}) {
		return await axios({
			url: "/api/reports/rec/bdx-details",
			method: "post",
			data: {
				...this._bdxCashbookDetailsBody(filters),
				...(undefined !== page ? { page } : null),
				...(undefined !== page_size ? { page_size } : null),
				...(undefined !== offset ? { offset } : null),
			},
			cancelToken,
		});
	}

	// Cashbook counterpart to getFCPReportV2BdxDetails - same reasoning.
	async getFCPReportV2CashbookDetails({ page, page_size, offset, cancelToken, ...filters } = {}) {
		return await axios({
			url: "/api/reports/rec/cashbook-details",
			method: "post",
			data: {
				...this._bdxCashbookDetailsBody(filters),
				...(undefined !== page ? { page } : null),
				...(undefined !== page_size ? { page_size } : null),
				...(undefined !== offset ? { offset } : null),
			},
			cancelToken,
		});
	}

	// "Export data" as a finished .xlsx built on the server (LIQ-2403) - same body as the matching
	// details call above, every matching row. responseType "blob" keeps the file bytes from being
	// parsed as JSON (an error body then arrives as a Blob too - callers turn it back into JSON).
	async exportFCPReportV2VarianceDetails({ sort_by, sort_dir, ...filters } = {}) {
		return await axios({
			url: "/api/reports/rec/variance-details/export",
			method: "post",
			data: { ...this._varianceDetailsBody(filters), sort_by, sort_dir },
			responseType: "blob",
		});
	}

	async exportFCPReportV2BdxDetails(filters = {}) {
		return await axios({
			url: "/api/reports/rec/bdx-details/export",
			method: "post",
			data: this._bdxCashbookDetailsBody(filters),
			responseType: "blob",
		});
	}

	async exportFCPReportV2CashbookDetails(filters = {}) {
		return await axios({
			url: "/api/reports/rec/cashbook-details/export",
			method: "post",
			data: this._bdxCashbookDetailsBody(filters),
			responseType: "blob",
		});
	}

	async getEmployeesReportProduction({
		department,
		min_salary,
		max_salary,
		hire_date_start,
		hire_date_end,
		sort_by,
		sort_dir,
	} = {}) {
		return await axios({
			url: "/api/reports/employees",
			params: {
				...(department ? { department } : null),
				...(min_salary ? { min_salary } : null),
				...(max_salary ? { max_salary } : null),
				...(hire_date_start ? { hire_date_start } : null),
				...(hire_date_end ? { hire_date_end } : null),
				...(sort_by ? { sort_by } : null),
				...(sort_dir ? { sort_dir } : null),
			},
		});
	}
}

export default new ReconciliationService();
